
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Attendee } from '@/types/attendee';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const usePaymentToggle = (attendee: Attendee) => {
  const [isPaid, setIsPaid] = useState<boolean>(attendee.paid);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState<boolean | null>(null);
  const { toast } = useToast();

  // Update local state when attendee props change
  useEffect(() => {
    setIsPaid(attendee.paid);
  }, [attendee.paid]);

  // Optimized handler using useCallback
  const handlePaymentToggleRequest = useCallback((checked: boolean) => {
    console.log(`Payment toggle requested for ${attendee.code}: ${checked ? 'paid' : 'not paid'}`);
    // Store pending status and show confirmation dialog
    setPendingPaymentStatus(checked);
    setShowConfirmDialog(true);
  }, [attendee.code]);

  // Optimized confirmation handler
  const confirmPaymentChange = useCallback(async () => {
    if (pendingPaymentStatus === null) return;
    
    console.log(`Confirming payment change for ${attendee.code} to ${pendingPaymentStatus ? 'paid' : 'not paid'}`);
    
    try {
      setIsUpdating(true);
      
      // Save t_shirt_size before update to ensure it's preserved
      const currentTShirtSize = attendee.t_shirt_size;
      console.log(`Current t_shirt_size before payment update: ${currentTShirtSize}`);
      
      // Now we'll use a more direct update approach that explicitly preserves the t_shirt_size
      const { data, error } = await supabase
        .from('attendees')
        .update({ 
          paid: pendingPaymentStatus,
          t_shirt_size: currentTShirtSize // Explicitly preserve t-shirt size
        })
        .eq('code', attendee.code)
        .select();
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        setIsPaid(pendingPaymentStatus);
        
        toast({
          title: pendingPaymentStatus ? "Đã xác nhận thanh toán" : "Đã đánh dấu chưa thanh toán",
          description: `Đã cập nhật trạng thái thanh toán cho ${attendee.name}`,
          variant: pendingPaymentStatus ? "default" : "destructive"
        });
        
        // Create optimized event data object with preserved t_shirt_size
        const updatedAttendee = {
          ...attendee,
          paid: pendingPaymentStatus,
          t_shirt_size: currentTShirtSize // Explicitly preserve t_shirt_size
        };
        
        console.log(`Preserved t_shirt_size in update event: ${updatedAttendee.t_shirt_size}`);
        
        // Dispatch event to update UI elsewhere
        window.dispatchEvent(new CustomEvent('payment-status-updated', {
          detail: { 
            attendeeCode: attendee.code, 
            paid: pendingPaymentStatus,
            updatedAttendee
          }
        }));
        
        // Dispatch additional database-update-success event for consistency
        window.dispatchEvent(new CustomEvent('database-update-success', {
          detail: {
            attendeeCode: attendee.code,
            updatedAttendee,
            type: 'payment-update'
          }
        }));
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast({
        title: "Lỗi cập nhật",
        description: "Không thể cập nhật trạng thái thanh toán. Vui lòng thử lại sau.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
      setPendingPaymentStatus(null);
      setShowConfirmDialog(false);
    }
  }, [pendingPaymentStatus, attendee, toast]);

  // Optimized cancel handler
  const cancelPaymentChange = useCallback(() => {
    setPendingPaymentStatus(null);
    setShowConfirmDialog(false);
  }, []);

  // Memoize the returned object to prevent unnecessary re-renders
  const methods = useMemo(() => ({
    isPaid,
    isUpdating,
    showConfirmDialog,
    setShowConfirmDialog,
    pendingPaymentStatus,
    handlePaymentToggleRequest,
    confirmPaymentChange,
    cancelPaymentChange
  }), [
    isPaid, 
    isUpdating, 
    showConfirmDialog, 
    pendingPaymentStatus, 
    handlePaymentToggleRequest, 
    confirmPaymentChange, 
    cancelPaymentChange
  ]);
  
  return methods;
};
